/**
 * Ableton-style Performance clip assembly.
 * One looping pattern whose track slots are filled from (possibly different)
 * source patterns — so a single PatternPlayer can run the session.
 */
import {
  emptyCell,
  findPattern,
  patternSteps,
  type ClipIR,
  type Pattern,
  type PatternCell,
  type PatternTrack,
} from "../../../../src/lib/audio";

export function gcd(a: number, b: number): number {
  let x = Math.abs(Math.floor(a));
  let y = Math.abs(Math.floor(b));
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

export function lcm(a: number, b: number): number {
  const x = Math.max(1, Math.floor(a));
  const y = Math.max(1, Math.floor(b));
  return Math.floor((x / gcd(x, y)) * y);
}

/** LCM of positive integers, capped to avoid runaway periods. */
export function lcmMany(values: number[], cap = 256): number {
  const nums = values.map(v => Math.max(1, Math.floor(v))).filter(v => v > 0);
  if (nums.length === 0) {
    return 16;
  }
  let out = nums[0]!;
  for (let i = 1; i < nums.length; i++) {
    out = lcm(out, nums[i]!);
    if (out > cap) {
      return cap;
    }
  }
  return Math.min(cap, out);
}

function cloneCell(cell: PatternCell | undefined): PatternCell {
  if (!cell) {
    return emptyCell();
  }
  return structuredClone(cell);
}

/** Repeat / truncate source cells into `length` steps. */
export function tileCells(source: PatternCell[], length: number): PatternCell[] {
  const len = Math.max(1, Math.floor(length));
  if (source.length === 0) {
    return Array.from({ length: len }, () => emptyCell());
  }
  return Array.from({ length: len }, (_, i) => cloneCell(source[i % source.length]));
}

export type BuildPerformanceClipResult = {
  clip: ClipIR;
  quantumSteps: number;
  /** Per-lane source pattern length (1 when idle). */
  lanePeriods: number[];
};

/**
 * Build a looping ClipIR for Performance mode.
 * `lanePatternIds[i]` = pattern providing track `i`, or null for silence.
 */
export function buildPerformanceClip(
  source: ClipIR,
  lanePatternIds: Array<string | null>,
): BuildPerformanceClipResult {
  const trackCount = Math.max(
    1,
    lanePatternIds.length,
    source.patterns[0]?.tracks.length ?? 0,
  );
  const periods: number[] = [];
  const activeLengths: number[] = [];

  for (let i = 0; i < trackCount; i++) {
    const pid = lanePatternIds[i] ?? null;
    if (!pid) {
      periods.push(1);
      continue;
    }
    const pattern = findPattern(source, pid);
    const steps = Math.max(1, patternSteps(pattern));
    periods.push(steps);
    activeLengths.push(steps);
  }

  const quantumSteps = activeLengths.length > 0
    ? lcmMany(activeLengths)
    : Math.max(16, patternSteps(source.patterns[0]) || 16);

  const tracks: PatternTrack[] = [];
  for (let i = 0; i < trackCount; i++) {
    const pid = lanePatternIds[i] ?? null;
    const templateName = source.patterns[0]?.tracks[i]?.name;
    if (!pid) {
      tracks.push({
        slot: i,
        ...(templateName ? { name: templateName } : {}),
        muted: true,
        cells: Array.from({ length: quantumSteps }, () => emptyCell()),
      });
      continue;
    }
    const pattern = findPattern(source, pid);
    const srcTrack = pattern?.tracks[i];
    const cells = tileCells(srcTrack?.cells ?? [], quantumSteps);
    tracks.push({
      slot: i,
      name: srcTrack?.name ?? templateName,
      muted: false,
      cells,
    });
  }

  const performancePattern: Pattern = {
    id: "PERF",
    name: "Performance",
    steps: quantumSteps,
    tracks,
  };

  const clip: ClipIR = {
    bpm: source.bpm,
    stepDivision: source.stepDivision,
    loop: true,
    patterns: [performancePattern],
    scenes: [{ id: "S0", name: "Performance", order: ["PERF"] }],
    order: ["S0"],
  };

  return { clip, quantumSteps, lanePeriods: periods };
}
